import React, { useState } from 'react';
import { TextField, Button, Box, Typography, Alert } from '@mui/material';
import { apiFetch } from './api';
import { startAuthentication } from '@simplewebauthn/browser';

export default function LoginForm({ onLogin }) {
  const [step, setStep] = useState('username'); // 'username', 'password', 'totp', 'webauthn'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authMethods, setAuthMethods] = useState(null);

  const checkAuthMethods = async (user) => {
    try {
      const response = await apiFetch('/auth/check', {
        method: 'POST',
        body: { username: user }
      });
      setAuthMethods(response);
      
      // Decide next step based on available auth methods
      if (response.has_webauthn) {
        setStep('webauthn');
      } else if (response.has_password) {
        setStep('password');
      } else {
        setError('No authentication method configured for this account');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }
    setError('');
    await checkAuthMethods(username);
  };

  const handleWebAuthnLogin = async () => {
    setLoading(true);
    setError('');
    try {
      // Start WebAuthn authentication
      const optionsResponse = await apiFetch('/webauthn/login/start', {
        method: 'POST',
        body: { username }
      });
      
      // Prompt user for WebAuthn credential
      const credential = await startAuthentication(optionsResponse);
      
      // Complete authentication
      await apiFetch('/webauthn/login/finish', {
        method: 'POST',
        body: credential
      });
      
      onLogin();
    } catch (err) {
      setError(err.message || 'WebAuthn authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch('/login', {
        method: 'POST',
        body: { username, password }
      });
      
      if (response.totp_required) {
        // TOTP required, move to TOTP step
        setStep('totp');
      } else {
        // Login successful
        onLogin();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await apiFetch('/login', {
        method: 'POST',
        body: { username, password, totp_code: totpCode }
      });
      onLogin();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setError('');
    if (step === 'totp') {
      setStep('password');
      setTotpCode('');
    } else if (step === 'password' || step === 'webauthn') {
      setStep('username');
      setPassword('');
      setAuthMethods(null);
    }
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4, p: 3, border: '1px solid #ccc', borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>Login</Typography>
      
      {step === 'username' && (
        <form onSubmit={handleUsernameSubmit}>
          <TextField
            label="Username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoComplete="off"
            spellCheck={false}
            inputMode="text"
            autoFocus
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ mt: 2 }}>
            Continue
          </Button>
        </form>
      )}

      {step === 'password' && (
        <form onSubmit={handlePasswordSubmit}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Logged in as: <strong>{username}</strong>
          </Typography>
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            autoComplete="off"
            spellCheck={false}
            inputMode="text"
            autoFocus
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ mt: 2 }}>
            {loading ? 'Logging in...' : 'Login'}
          </Button>
          <Button onClick={handleBack} variant="text" fullWidth sx={{ mt: 1 }}>
            Back
          </Button>
        </form>
      )}

      {step === 'totp' && (
        <form onSubmit={handleTotpSubmit}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Logged in as: <strong>{username}</strong>
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter the 6-digit code from your authenticator app
          </Alert>
          <TextField
            label="TOTP Code"
            value={totpCode}
            onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            fullWidth
            margin="normal"
            required
            autoComplete="off"
            spellCheck={false}
            inputMode="numeric"
            autoFocus
            placeholder="000000"
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading || totpCode.length !== 6} sx={{ mt: 2 }}>
            {loading ? 'Verifying...' : 'Verify'}
          </Button>
          <Button onClick={handleBack} variant="text" fullWidth sx={{ mt: 1 }}>
            Back
          </Button>
        </form>
      )}

      {step === 'webauthn' && (
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Logged in as: <strong>{username}</strong>
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Use your security key to authenticate
          </Alert>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          <Button 
            onClick={handleWebAuthnLogin} 
            variant="contained" 
            color="primary" 
            fullWidth 
            disabled={loading} 
            sx={{ mt: 2 }}
          >
            {loading ? 'Authenticating...' : 'Authenticate with Security Key'}
          </Button>
          <Button onClick={handleBack} variant="text" fullWidth sx={{ mt: 1 }}>
            Back
          </Button>
        </Box>
      )}
    </Box>
  );
}
